/**
 * useTaksasi Hook
 * 
 * Custom React hook for Taksasi data management and state
 */

import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import TaksasiApiService from '../services/taksasiApiService';
import {
  TaksasiCompanyGroup,
  TaksasiFilters,
  PaginationMeta,
  TaksasiStats,
} from '../types';
import { calculateStats } from '../utils/utils';
import { TAKSASI_DEFAULTS } from '../apiConfig';

/**
 * Hook return type
 */
interface UseTaksasiReturn {
  data: TaksasiCompanyGroup[];
  stats: TaksasiStats;
  pagination: PaginationMeta;
  loading: boolean;
  error: string | null;
  filters: TaksasiFilters;
  setFilters: (filters: TaksasiFilters) => void;
  refreshData: () => void;
  goToPage: (page: number) => void;
  setLimit: (limit: number) => void;
}

/**
 * Custom hook for managing Taksasi data and state
 */
export const useTaksasi = (): UseTaksasiReturn => {
  const toast = useToast();
  
  // Get default date range (last 7 days)
  const getDefaultDateRange = () => {
    const today = new Date();
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);
    
    return {
      date_from: today.toISOString().split('T')[0],
      date_to: today.toISOString().split('T')[0],
    };
  };
  
  const [data, setData] = useState<TaksasiCompanyGroup[]>([]);
  const [stats, setStats] = useState<TaksasiStats>({
    total_records: 0,
    total_planted_area: 0,
    total_est_output: 0,
    avg_bjr: 0,
    avg_akp: 0,
    total_est_ripe_bunch: 0,
  });
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: TAKSASI_DEFAULTS.PAGE,
    limit: TAKSASI_DEFAULTS.LIMIT,
    total_items: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TaksasiFilters>({
    ...getDefaultDateRange(),
    page: TAKSASI_DEFAULTS.PAGE,
    limit: TAKSASI_DEFAULTS.LIMIT,
    company_id: 1,
  });

  /**
   * Fetch Taksasi data from API
   */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await TaksasiApiService.getList(filters);
      
      if (response.success) {
        setData(response.data);
        setPagination(response.pagination);
        setStats(calculateStats(response.data));
      } else {
        throw new Error('Failed to fetch Taksasi data');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Gagal mengambil data Taksasi';
      setError(errorMessage);
      
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  /**
   * Effect to fetch data when filters change
   */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Refresh data manually
   */
  const refreshData = useCallback(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Navigate to specific page
   */
  const goToPage = useCallback((page: number) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  /**
   * Change items per page limit
   */
  const setLimit = useCallback((limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 1 }));
  }, []);

  return {
    data,
    stats,
    pagination,
    loading,
    error,
    filters,
    setFilters,
    refreshData,
    goToPage,
    setLimit,
  };
};

export default useTaksasi;
